import { Divider, EHeadingStyleVariant, Heading } from '@outdoorsyco/bonfire';
import cn from 'classnames';
import camelCase from 'lodash/camelCase';
import flatten from 'lodash/flatten';
import startCase from 'lodash/startCase';
import values from 'lodash/values';
import React, { useEffect } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';

import Breadcrumbs from '@/components/common/Breadcrumbs';
import MaxWidth from '@/components/common/layout/MaxWidth';
import Parks from '@/components/common/Parks';
import ErrorBoundary from '@/components/ErrorBoundary';
import CrossLinks from '@/components/localities/CrossLinks';
import Description from '@/components/localities/Description';
import { FAQs } from '@/components/localities/FAQs';
import { LocalityHead } from '@/components/localities/Head';
import HowToOutdoorsy from '@/components/localities/HowToOutdoorsy';
import NearbyCampgrounds from '@/components/localities/NearbyCampgrounds';
import NearbyRVRentalContent from '@/components/localities/NearbyRVRentalContent';
import NearbyTrails from '@/components/localities/NearbyTrails';
import RentalFilters from '@/components/localities/RentalFilters';
import { Reviews } from '@/components/localities/Reviews';
import { RichTextSection } from '@/components/localities/RichTextSection';
import SearchComponent from '@/components/localities/SearchComponent';
import { SearchQuerySection } from '@/components/localities/SearchQuerySection';
import SeeWhatsAvailable from '@/components/localities/SeeWhatsAvailable';
import NewsLetterSignup from '@/components/switchback/NewsLetterSignup/NewsLetterSignup';
import { ESearchFilters } from '@/constants/searchFilters';
import { SEO_VEHICLES } from '@/constants/vehicleDetails';
import { setHeaderSearchFilter } from '@/redux/modules/globalHeader';
import { trackEvent } from '@/services/track-event';

const vowels = ['a', 'e', 'i', 'o', 'u'];

// some of the search page filters use camelCase (like unlimitedMiles)
// event though they later get sent to BE in snake_case
export const filtersToCamelCase = filters => {
  if (!filters) return {};

  const searchFilters = { ...filters };

  if (searchFilters.unlimited_miles !== undefined) {
    searchFilters[ESearchFilters.UNLIMITED_MILES] = searchFilters.unlimited_miles;
    delete searchFilters.unlimited_miles;
  }

  return searchFilters;
};

export const getHeroTitleNoLines = (title, locality_type, locationName, isTestGroup) => {
  if (locality_type === 'campground') {
    return title;
  }

  if (isTestGroup) {
    const formattedTitle = startCase(camelCase(title.trim()));
    return <>Deals on RV Rentals in {formattedTitle}: Find Motorhomes, Camper Vans & More</>;
  }

  return <>RV rental {locationName}: Motorhome rentals, camper van rentals and more</>;
};

const LocalityTemplate = props => {
  const intl = useIntl();
  const { keyword, localitySearchFilters } = props;

  const localityActiveFilters = filtersToCamelCase(localitySearchFilters);

  const {
    title,
    introduction,
    sections,
    breadcrumbs,
    nearby,
    keywords,
    region_code,
    locality_type,
    links,
    locale,
    url,
    url_path,
    faqs,
    region_name,
    boundsFormatted,
    localityRentalReviews,
    how_to_rent,
  } = props.metadata || {};

  const dispatch = useDispatch();

  const campgroundLinks =
    locality_type === 'campground' ? links?.['locality-campground-directory'] : false;

  const parkLinks = locality_type === 'park' ? links?.['locality-park-directory'] : false;

  const relatedLinks = !campgroundLinks && !parkLinks && links ? flatten(values(links)) : false;

  const rvType = SEO_VEHICLES[keyword?.slug] ? keyword.title : 'RV';

  const howToRentTitle = title || 'Outdoorsy';

  const locationName = region_code ? `${title}, ${region_code}` : `${title}`;

  const howToRentDescription = how_to_rent?.length > 0 ? how_to_rent[0].description : '';

  useEffect(() => {
    dispatch(
      setHeaderSearchFilter({
        address: locationName,
      }),
    );
  }, [dispatch, locationName]);

  useEffect(() => {
    trackEvent({
      event: 'Locality : Viewed page',
      pageType: 'Locality SRP',
    });
  }, [dispatch]);

  const localityKeywordNearby = ((keyword && nearby?.locality_nearby_rentals) || []).filter(
    nearbyLocality => nearbyLocality.keyword === keyword.slug,
  );
  const countyKeywordNearby = ((keyword && nearby?.county_nearby_rentals) || []).filter(
    nearbyLocality => nearbyLocality.keyword === keyword.slug,
  );
  const hasLocalityKeywordNearby = localityKeywordNearby.length !== 0;
  const hasCountyKeywordNearby = countyKeywordNearby.length !== 0;

  const hikingTrails = nearby?.trail?.map(trail => trail.title);

  const heroTitleNoLines = getHeroTitleNoLines(
    title,
    locality_type,
    locationName,
    props.isTestGroup,
  );

  return (
    <>
      <div className="flex flex-col">
        <SearchComponent
          initialParams={localityActiveFilters}
          bounds={boundsFormatted}
          breadcrumbs={breadcrumbs}
          searchTitle={heroTitleNoLines}
        />
      </div>

      <div className="flex flex-col items-center">
        <LocalityHead
          cityName={title}
          regionCode={region_code}
          stateName={region_name}
          rvParkDestination={introduction}
          hikingTrails={hikingTrails}
        />

        <MaxWidth className={`px-4 md:pt-10`}>
          <Divider />
        </MaxWidth>

        {!keyword && introduction && (
          <MaxWidth className="py-10 gap-6">
            <div className="lg:max-w-60 lg:mx-auto">
              <Heading
                variant={EHeadingStyleVariant.H4}
                component="h2"
                className="mb-6 text-center">
                <FormattedMessage
                  defaultMessage="Discover {locality} RV rentals for memorable road trips"
                  values={{ locality: title }}
                  id="z+dE6i"
                />
              </Heading>

              <Description introduction={introduction} />
            </div>
          </MaxWidth>
        )}

        <div className="w-full py-10">
          <MaxWidth className="mx-auto">
            <Heading variant={EHeadingStyleVariant.H4} component="h2" className="mb-6 text-center">
              <FormattedMessage defaultMessage="Find your personal RV rental style" id="7JH5c7" />
            </Heading>

            <SeeWhatsAvailable address={locationName} />
          </MaxWidth>
        </div>

        <div className="w-full py-6 lg:py-10 bg-canvas-100">
          <MaxWidth className="mx-auto">
            <Heading variant={EHeadingStyleVariant.H4} component="h2" className="mb-6 text-center">
              {vowels.includes(rvType.toLowerCase().charAt(0))
                ? intl.formatMessage(
                    { defaultMessage: 'How to rent an {rv_Type} in {locationName}', id: 'm+b88e' },
                    { rv_Type: rvType, locationName: howToRentTitle },
                  )
                : intl.formatMessage(
                    { defaultMessage: 'How to rent a {rv_Type} in {locationName}', id: 'bUUD8R' },
                    { rv_Type: rvType, locationName: howToRentTitle },
                  )}
            </Heading>

            <HowToOutdoorsy title={howToRentTitle} description={howToRentDescription} />
          </MaxWidth>
        </div>

        {!keyword && !!sections?.length && (
          <div className="w-full">
            {sections.map((section, index) => {
              const { section_type = 'rich_text', background = 'white', divider = false } = section;
              const { background: prevSectionBackground = 'white' } = sections[index - 1] || {};

              return (
                <ErrorBoundary key={index} fallBackComponent={null}>
                  <div
                    className={cn('w-full pb-10', {
                      ['pt-10']: !index || prevSectionBackground !== background,
                      ['bg-white']: background === 'white',
                      ['bg-canvas-100']: background === 'canvas',
                    })}>
                    <MaxWidth className="mx-auto">
                      {(section_type === 'search_query' && <SearchQuerySection {...section} />) || (
                        <RichTextSection {...section} />
                      )}

                      {divider && <Divider className="mt-10" />}
                    </MaxWidth>
                  </div>
                </ErrorBoundary>
              );
            })}
          </div>
        )}

        {!keyword && !!keywords?.length && (
          <MaxWidth className="py-10 gap-6">
            {!!keywords?.length && <RentalFilters keywords={keywords} />}
          </MaxWidth>
        )}

        <div className="w-full bg-white">
          <MaxWidth className="m-auto my-20">
            <NewsLetterSignup
              title={intl.formatMessage({
                defaultMessage:
                  'Be the first to get doses of destination inspiration, and discount codes.',
                id: 'RdFytN',
              })}
              source="Locality SRP"
            />
          </MaxWidth>
        </div>

        {nearby && nearby.trail && nearby.campground && (
          <div className="w-full py-10 bg-canvas-100">
            <MaxWidth className="mx-auto">
              <Heading
                variant={EHeadingStyleVariant.H4}
                component="h2"
                className="mb-6 text-center">
                <FormattedMessage
                  defaultMessage="Popular nearby hiking trails and campgrounds"
                  id="wSRwEo"
                />
              </Heading>

              <div className="flex flex-col py-8 lg:flex-row lg:justify-between">
                <NearbyTrails trails={nearby.trail} />
                <div className="w-32 h-6" />
                <NearbyCampgrounds campgrounds={nearby.campground} />
              </div>
            </MaxWidth>
          </div>
        )}

        {!!localityRentalReviews?.length && (
          <div className="w-full py-10 xl:px-4 xl:max-w-xxl xl:mx-auto">
            <Heading variant={EHeadingStyleVariant.H4} component="h2" className="mb-6 text-center">
              <FormattedMessage
                defaultMessage="Recent RV rental reviews in {locality}"
                values={{ locality: title }}
                id="bOCPhg"
              />
            </Heading>

            <Reviews
              url={url}
              urlPath={url_path}
              reviews={localityRentalReviews}
              localityName={title}
            />
          </div>
        )}

        <MaxWidth className="py-10">
          <Heading variant={EHeadingStyleVariant.H4} component="h2" className="mb-6 text-center">
            <FormattedMessage
              defaultMessage="Frequently asked questions about renting an RV near {locality}"
              values={{ locality: title }}
              id="SESQZp"
            />
          </Heading>

          <FAQs items={faqs} rvType={rvType} />
        </MaxWidth>

        <div className="w-full py-10 mb-10 bg-canvas-100">
          <MaxWidth collapseMobile id="rentals" className="mx-auto">
            <Heading variant={EHeadingStyleVariant.H4} component="h2" className="mb-6 text-center">
              <FormattedMessage defaultMessage="Discover the best of what's around" id="S0ya4u" />
            </Heading>

            <Parks locale={locale} />
          </MaxWidth>
        </div>

        {nearby && (
          <React.Fragment>
            <MaxWidth className="mb-10">
              <Heading
                variant={EHeadingStyleVariant.H4}
                component="h2"
                className="mb-6 text-center">
                <FormattedMessage defaultMessage="Popular Nearby Cities" id="frI5QK" />
              </Heading>

              <div data-testid="nearby-cities" className="flex-row w-full xs:flex xs:flex-wrap">
                <NearbyRVRentalContent
                  rvType={rvType}
                  locations={hasLocalityKeywordNearby ? localityKeywordNearby : nearby.locality}
                />
              </div>
            </MaxWidth>

            <MaxWidth className="mb-10">
              <Heading
                variant={EHeadingStyleVariant.H4}
                component="h2"
                className="mb-6 text-center">
                <FormattedMessage defaultMessage="Popular Nearby Counties" id="J4AwQr" />
              </Heading>

              <div data-testid="nearby-counties" className="flex-row w-full xs:flex xs:flex-wrap">
                <NearbyRVRentalContent
                  rvType={rvType}
                  locations={hasCountyKeywordNearby ? countyKeywordNearby : nearby.county}
                />
              </div>
            </MaxWidth>

            {!hasLocalityKeywordNearby && !hasCountyKeywordNearby && nearby.park && (
              <MaxWidth className="mb-10">
                <Heading
                  variant={EHeadingStyleVariant.H4}
                  component="h2"
                  className="mb-6 text-center">
                  <FormattedMessage defaultMessage="Popular Nearby Parks" id="38MBA9" />
                </Heading>
                <div className="flex-row w-full xs:flex xs:flex-wrap">
                  <NearbyRVRentalContent rvType={rvType} locations={nearby.park} />
                </div>
              </MaxWidth>
            )}
          </React.Fragment>
        )}

        {!hasLocalityKeywordNearby && !hasCountyKeywordNearby && relatedLinks && (
          <MaxWidth className="mb-10">
            <Heading variant={EHeadingStyleVariant.H4} component="h2" className="mb-6 text-center">
              <FormattedMessage
                defaultMessage="RV Parks, Campgrounds, and State Parks near {locality}"
                values={{ locality: title }}
                id="1J60tO"
              />
            </Heading>

            <div data-testid="nearby-parks" className="flex-row w-full xs:flex xs:flex-wrap">
              <CrossLinks links={relatedLinks} />
            </div>
          </MaxWidth>
        )}

        {campgroundLinks && (
          <MaxWidth className="mb-10">
            <Heading variant={EHeadingStyleVariant.H4} component="h2" className="mb-6 text-center">
              <FormattedMessage
                defaultMessage="RV Parks and Campgrounds near {locality}"
                values={{ locality: title }}
                id="oS4K5o"
              />
            </Heading>

            <div data-testid="nearby-campgrounds" className="flex-row w-full xs:flex xs:flex-wrap">
              <CrossLinks links={campgroundLinks} />
            </div>
          </MaxWidth>
        )}

        {parkLinks && (
          <MaxWidth className="mb-10">
            <Heading variant={EHeadingStyleVariant.H4} component="h2" className="mb-6 text-center">
              <FormattedMessage
                defaultMessage="State Parks near {locality}"
                values={{ locality: title }}
                id="amtmvb"
              />
            </Heading>

            <div className="flex-row w-full xs:flex xs:flex-wrap">
              <CrossLinks links={parkLinks} />
            </div>
          </MaxWidth>
        )}

        <MaxWidth className="mb-10">
          <div className="flex justify-center">
            <Breadcrumbs breadcrumbs={breadcrumbs} />
          </div>
        </MaxWidth>
      </div>
    </>
  );
};

export default LocalityTemplate;
